import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Chip,
    CircularProgress,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import Navbar from "../../Navbar/Navbar";
import { toast } from "react-toastify";
import baseURL from "../../config";

const AssignedCrewCvVesselowner = () => {
    const [vesselOwners, setVesselOwners] = useState([]);
    const [approvedCrewCVs, setApprovedCrewCVs] = useState([]);
    const [selectedVesselOwners, setSelectedVesselOwners] = useState([]);
    const [selectedCVs, setSelectedCVs] = useState(new Set()); // CVs you want to assign
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("token");

        const fetchData = async () => {
            try {
                // fetch vessel owners
                const ownersRes = await axios.get(
                    `${baseURL}/api/vesselOwner/getVesselOwnerALL`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setVesselOwners(ownersRes.data.data || []);

                // fetch approved crew CVs
                const cvsRes = await axios.get(
                    `${baseURL}/api/crewCVuploadByCrewingAgentRoute/approved`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setApprovedCrewCVs(cvsRes.data?.data || []);
                console.log("cvsRes.data.data", cvsRes.data.data);
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to fetch data");
            }
        };

        fetchData();
    }, []);

    // ✅ Dialog Handlers
    const handleOpenAssignDialog = () => setOpenAssignDialog(true);
    const handleCloseAssignDialog = () => setOpenAssignDialog(false);

    // ✅ Assign CVs
    const handleAssignCVs = async () => {
        if (selectedVesselOwners.length === 0) {
            toast.warning("Please select at least one vessel owner");
            return;
        }

        setAssignLoading(true);
        const token = localStorage.getItem("token");

        try {
            // extract only _id from approvedCrewCVs
            const allIds = approvedCrewCVs.map((item) => item._id);

            const payload = {
                crewIds: allIds, // 👈 send all parent _ids
                vesselOwnerIds: selectedVesselOwners, // IDs of selected vessel owners
            };

            const res = await axios.post(
                `${baseURL}/api/crewCvAssigned/assign-cv`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("CVs assigned successfully!");
            setOpenAssignDialog(false);
            setSelectedVesselOwners([]);
        } catch (error) {
            console.error("Error assigning CVs:", error);
            toast.error("Failed to assign CVs");
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <>
            <Navbar />

            {/* Centered Assign Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleOpenAssignDialog}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "red",
                        "&:hover": {
                            backgroundColor: "darkred",
                        },
                    }}
                >
                    Assign to Vessel Owner
                </Button>
            </Box>

            {/* Assignment Dialog */}
            <Dialog
                open={openAssignDialog}
                onClose={handleCloseAssignDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: "#1e293b" }}>
                    Assign CVs to Vessel Owner
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
                            You have selected {selectedCVs.size} CV(s) to assign.
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Select Vessel Owners</InputLabel>
                            <Select
                                multiple
                                value={selectedVesselOwners}
                                onChange={(e) => setSelectedVesselOwners(e.target.value)}
                                label="Select Vessel Owners"
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const owner = vesselOwners.find(
                                                (o) => o._id === value
                                            );
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={
                                                        owner?.company_name ||
                                                        owner?.companyName ||
                                                        "Unknown"
                                                    }
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#fee2e2",
                                                        color: "darkred",
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {vesselOwners.map((owner) => (
                                    <MenuItem key={owner._id} value={owner._id}>
                                        <Checkbox
                                            checked={selectedVesselOwners.indexOf(owner._id) > -1}
                                            sx={{
                                                color: "red",
                                                "&.Mui-checked": {
                                                    color: "red",
                                                },
                                            }}
                                        />
                                        <Typography>
                                            {owner?.company_name || owner.companyName}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                        onClick={handleCloseAssignDialog}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: "red",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssignCVs}
                        variant="contained"
                        disabled={assignLoading || selectedVesselOwners.length === 0}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: "red",
                            "&:hover": {
                                backgroundColor: "darkred",
                            },
                        }}
                    >
                        {assignLoading ? (
                            <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                            "Assign"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AssignedCrewCvVesselowner;
